// Impresión del formato de campo de un muestreo (calidad ERP, como RE-08).
// Documento HTML dedicado en iframe con CSS embebido — no depende de Tailwind.
// Reproduce el formato Excel: encabezado + tabla de secciones con columnas del
// tipo (cabecera de 2 niveles por grupo) + totales + firma del muestreador.

interface Columna { key: string; label: string; grupo?: string; sub?: string; }
interface Grupo { grupo: string; cols: Columna[] }
interface Fila {
  lote_num: string; seccion: string; area_ha: number | null; plantas: number | null;
  muestra: number | null; datos: Record<string, string>;
}

export interface FormatoOpts {
  muestreo: any;
  tipoCodigo?: string;
  tipoNombre: string;
  ancla: string | null;
  cols: Columna[];
  grupos: Grupo[];
  filas: Fila[];
  observaciones: string;
  usuario: string;
  empresaNombre: string;
}

const esc = (s: any) => String(s ?? '').replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c] as string));
const fmtFecha = (iso?: string | null) =>
  iso ? new Date(iso + 'T00:00:00').toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '________';

// Abre el HTML en iframe oculto e imprime.
function imprimirHtml(html: string) {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) { document.body.removeChild(iframe); return; }
  doc.open(); doc.write(html); doc.close();
  setTimeout(() => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  }, 400);
}

export function imprimirFormatoMuestreo(opts: FormatoOpts) {
  // Layout FIEL por tipo (réplica del formato de papel que usa el muestreador).
  // Los que aún no tienen layout dedicado caen al genérico.
  if (opts.tipoCodigo === 'estimaciones') { imprimirHtml(buildEstimacionesHtml(opts)); return; }

  const { muestreo, tipoNombre, ancla, cols, grupos, filas, observaciones, usuario, empresaNombre } = opts;
  const hoyStr = new Date().toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const anclaLabel = ancla === 'ddf' ? 'DDF' : ancla === 'dds' ? 'DDS' : 'Grupo de forza';

  // Etiqueta corta de la columna (sin repetir el grupo)
  const colLabel = (c: Columna) => esc(c.sub || (c.grupo ? (c.label.replace(c.grupo, '').trim() || c.label) : c.label));

  const totalCol = (key: string) => filas.reduce((s, f) => s + (Number(f.datos[key]) || 0), 0);

  // Cabeceras: fila de grupos (colspan) + fila de columnas
  const thGrupos = grupos.map(g =>
    `<th colspan="${g.cols.length}" class="grp">${g.grupo ? esc(g.grupo) : '&nbsp;'}</th>`
  ).join('');
  const thCols = cols.map(c => `<th class="col">${colLabel(c)}</th>`).join('');

  const filasHtml = filas.map(f => `
    <tr>
      <td class="b">${esc(f.lote_num)}</td>
      <td class="b">${esc(f.seccion)}</td>
      <td class="num">${f.area_ha != null ? Number(f.area_ha).toFixed(2) : ''}</td>
      <td class="num">${f.plantas != null ? f.plantas.toLocaleString('es-CR') : ''}</td>
      <td class="num">${f.muestra ?? ''}</td>
      ${cols.map(c => `<td class="num">${esc(f.datos[c.key] ?? '')}</td>`).join('')}
    </tr>`).join('');

  const totalesHtml = `
    <tr class="tot">
      <td colspan="5" class="totlbl">Totales</td>
      ${cols.map(c => `<td class="num">${totalCol(c.key) || ''}</td>`).join('')}
    </tr>`;

  const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8">
<title>${esc(tipoNombre)} · Grupo ${esc(muestreo?.grupo_forza)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 0; padding: 18px 16px; font-size: 9pt; }
  .head { display: table; width: 100%; border-bottom: 2px solid #1a3a6c; padding-bottom: 6px; margin-bottom: 8px; }
  .head .l { display: table-cell; vertical-align: bottom; }
  .head .r { display: table-cell; text-align: right; vertical-align: bottom; font-size: 8pt; color: #555; }
  .empresa { font-size: 14pt; font-weight: bold; color: #003399; }
  .titulo { font-size: 12pt; font-weight: bold; margin-top: 2px; }
  .meta { display: table; width: 100%; margin: 6px 0 10px; font-size: 9pt; }
  .meta .cell { display: table-cell; padding: 2px 10px 2px 0; }
  .meta b { color: #000; }
  table.fmt { width: 100%; border-collapse: collapse; font-size: 8.5pt; }
  table.fmt th, table.fmt td { border: 1px solid #999; padding: 3px 4px; text-align: center; }
  table.fmt thead th { background: #c8d8f0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  table.fmt th.grp { background: #1a3a6c !important; color: #fff !important; font-size: 8pt; }
  table.fmt th.col { font-size: 7.5pt; vertical-align: bottom; line-height: 1.1; }
  table.fmt td.b { font-weight: bold; }
  table.fmt td.num { text-align: center; }
  table.fmt tr.tot td { font-weight: bold; background: #eef2f8 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  table.fmt tr.tot td.totlbl { text-align: right; }
  .firmas { display: table; width: 100%; margin-top: 30px; }
  .firmas .f { display: table-cell; width: 33%; text-align: center; font-size: 8pt; padding: 0 14px; }
  .firmas .line { border-top: 1px solid #000; margin-bottom: 3px; }
  .obs { margin-top: 12px; font-size: 8.5pt; }
  .foot { margin-top: 14px; padding-top: 5px; border-top: 1px solid #ccc; font-size: 7.5pt; color: #888; text-align: center; }
  @page { size: landscape; margin: 10mm; }
</style></head>
<body>
  <div class="head">
    <div class="l">
      <div class="empresa">${esc(empresaNombre || 'Control de Piña')}</div>
      <div class="titulo">${esc(tipoNombre)}</div>
    </div>
    <div class="r">Impreso: ${esc(hoyStr)}</div>
  </div>

  <div class="meta">
    <div class="cell"><b>${anclaLabel}:</b> ${esc(muestreo?.grupo_forza || '')}</div>
    <div class="cell"><b>Lote(s):</b> ${esc(muestreo?.lote_num || '')}</div>
    <div class="cell"><b>Semana:</b> ${esc(muestreo?.semana || '')}</div>
    <div class="cell"><b>Fecha programada:</b> ${fmtFecha(muestreo?.fecha_programada)}</div>
    <div class="cell"><b>Fecha muestreo:</b> ${muestreo?.ejecutada_at ? esc(new Date(muestreo.ejecutada_at).toLocaleDateString('es-CR')) : '________'}</div>
    <div class="cell"><b>Muestreador:</b> ${esc(usuario || '________')}</div>
  </div>

  <table class="fmt">
    <thead>
      <tr>
        <th rowspan="2">Lote</th>
        <th rowspan="2">Sec.</th>
        <th rowspan="2">Área</th>
        <th rowspan="2">Plantas</th>
        <th rowspan="2">Muestra</th>
        ${thGrupos}
      </tr>
      <tr>${thCols}</tr>
    </thead>
    <tbody>
      ${filasHtml}
      ${totalesHtml}
    </tbody>
  </table>

  ${observaciones ? `<div class="obs"><b>Observaciones:</b> ${esc(observaciones)}</div>` : ''}

  <div class="firmas">
    <div class="f"><div class="line"></div>Muestreador</div>
    <div class="f"><div class="line"></div>Revisado por</div>
    <div class="f"><div class="line"></div>Ingeniero</div>
  </div>

  <div class="foot">MYA ERP · Control de Piña — Labores y Muestreos</div>
</body></html>`;

  imprimirHtml(html);
}

// ════════════════════════════════════════════════════════════════════
// Layout FIEL: Estimaciones de calibres-rechazo (réplica del formato de papel).
// Cada sección/bloque es un mini-formulario repetido: fila Bloque/Fruta
// empaque/rechazo/total + tabla (N° hileras | Distribución por calibre | Rechazo).
// Pensado para imprimir EN BLANCO y llenar a mano en campo (deja celdas vacías).
// ════════════════════════════════════════════════════════════════════
function buildEstimacionesHtml(opts: FormatoOpts): string {
  const { muestreo, filas, usuario, empresaNombre } = opts;
  const CAL = ['4', '5', '6', '7', '8', '9', '10'];
  const RECH = ['Peq.', 'Natur.', 'Sin parir', 'Cosecha', 'Cuello', 'Rata', 'Conica', 'Corcho', 'Quema de sol', 'Deforme'];
  const RECH_KEYS = ['rech_peq', 'rech_natural', 'rech_sinparir', 'rech_cosecha', 'rech_cuello', 'rech_rata', 'rech_conica', 'rech_corcho', 'rech_quemasol', 'rech_deforme'];

  // Un bloque-formulario por sección. Si hay menos de 6, completar con vacíos
  // (el formato de papel trae 6 bloques por hoja para llenar a mano).
  const secciones = filas.length > 0 ? filas : [];
  const minBloques = Math.max(6, secciones.length);
  const bloques = [];
  for (let i = 0; i < minBloques; i++) {
    const f = secciones[i];
    const val = (k: string) => (f && f.datos[k] != null && f.datos[k] !== '' ? esc(f.datos[k]) : '');
    bloques.push(`
      <div class="bloque">
        <div class="brow">
          <span class="bcell"><b>Bloque</b> <span class="box">${f ? esc(f.seccion) : ''}</span></span>
          <span class="bcell">Fruta empaque <span class="box wide"></span></span>
          <span class="bcell">Fruta rechazo <span class="box wide"></span></span>
          <span class="bcell">Frutas totales <span class="box wide"></span></span>
        </div>
        <table class="est">
          <thead>
            <tr>
              <th rowspan="2" class="hil">N° de<br>hileras</th>
              <th colspan="${CAL.length}" class="grp">DISTRIBUCIÓN POR CALIBRES</th>
              <th colspan="${RECH.length}" class="grp">Rechazo</th>
            </tr>
            <tr>
              ${CAL.map(c => `<th>${c}</th>`).join('')}
              ${RECH.map(r => `<th class="rh">${esc(r)}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${f ? val('n_hileras') : ''}</td>
              ${CAL.map(c => `<td>${f ? val('cal_' + c) : ''}</td>`).join('')}
              ${RECH_KEYS.map(k => `<td>${f ? val(k) : ''}</td>`).join('')}
            </tr>
          </tbody>
        </table>
      </div>`);
  }

  return `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8">
<title>Estimaciones de calibres-rechazo</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #000; margin: 0; padding: 14px 18px; font-size: 9pt; }
  .head .emp { font-size: 12pt; font-weight: bold; }
  .head .dep { font-size: 9pt; font-weight: bold; }
  .head .tit { font-size: 11pt; font-weight: bold; text-align: center; margin: 2px 0 6px; }
  .meta { display: table; width: 100%; margin-bottom: 8px; font-size: 9pt; }
  .meta .r { display: table-row; }
  .meta .c { display: table-cell; padding: 1px 14px 1px 0; white-space: nowrap; }
  .meta .box { display: inline-block; min-width: 70px; border: 1px solid #000; padding: 0 6px; }
  .bloque { margin-bottom: 10px; break-inside: avoid; }
  .brow { display: flex; gap: 22px; align-items: center; font-size: 9pt; margin-bottom: 1px; }
  .brow .box { display: inline-block; min-width: 46px; border: 1px solid #000; padding: 0 4px; text-align: center; }
  .brow .box.wide { min-width: 70px; }
  table.est { width: 100%; border-collapse: collapse; }
  table.est th, table.est td { border: 1px solid #000; text-align: center; font-size: 8pt; padding: 2px 2px; }
  table.est th.grp { background: #e8e8e8; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  table.est th.hil { width: 56px; }
  table.est th.rh { font-size: 6.5pt; line-height: 1; }
  table.est tbody td { height: 22px; }
  .foot { margin-top: 10px; padding-top: 5px; border-top: 1px solid #ccc; font-size: 7.5pt; color: #888; text-align: center; }
  @page { size: portrait; margin: 12mm; }
</style></head>
<body>
  <div class="head">
    <div class="emp">${esc(empresaNombre || 'Agropecuaria Vasquez y Zuñiga S.A.')}</div>
    <div class="dep">DEPARTAMENTO DE SERVICIOS TECNICOS</div>
    <div class="tit">Estimaciones de calibres-rechazo</div>
  </div>
  <div class="meta">
    <div class="r">
      <span class="c"><b>Lote</b> <span class="box">${esc(muestreo?.lote_num || '')}</span></span>
      <span class="c">Fecha de muestreo <span class="box">${muestreo?.ejecutada_at ? esc(new Date(muestreo.ejecutada_at).toLocaleDateString('es-CR')) : ''}</span></span>
      <span class="c">Finca <span class="box">${esc(muestreo?.finca_nombre || '')}</span></span>
    </div>
    <div class="r">
      <span class="c"><b>GF</b> <span class="box">${esc(muestreo?.grupo_forza || '')}</span></span>
      <span class="c">Colaboradores <span class="box">${esc(usuario || '')}</span></span>
      <span class="c">Semana <span class="box">${esc(muestreo?.semana || '')}</span></span>
    </div>
  </div>
  ${bloques.join('')}
  <div class="foot">MYA ERP · Control de Piña — Estimaciones de calibres-rechazo</div>
</body></html>`;
}
